import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {
  setAccountSuspended,
  setCredentials,
  setToken,
  signOut,
} from '@/features/auth/store/authSlice';
import { notify, withCrossTabLock } from '@/lib/utils';

import type { RootState } from './index';

// ---------------------------------------------------------------------------
// Raw base query — token injection
// Auth header: "Bearer <key>" (DRF TokenAuthentication)
// ---------------------------------------------------------------------------

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  credentials: 'include',

  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// ---------------------------------------------------------------------------
// Envelope unwrapper
// All m-ai-check-api responses are wrapped: { success, data, message }
// Strip the envelope so RTK Query endpoints work with plain T, not ApiEnvelope<T>.
// ---------------------------------------------------------------------------

type ApiEnvelope<T> = { success: boolean; data: T; message: string };

export function isEnvelope(value: unknown): value is ApiEnvelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    'data' in value &&
    'message' in value
  );
}

const baseQueryWithEnvelope: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.data !== undefined && isEnvelope(result.data)) {
    return { data: result.data.data };
  }

  return result;
};

// ---------------------------------------------------------------------------
// Auth endpoints that should never trigger a token refresh on 401.
// Logout is not one of them: an expired access token must be refreshed so the
// API can still revoke the refresh cookie.
// ---------------------------------------------------------------------------

const AUTH_ENDPOINTS = [
  '/api/auth/v1/login/',
  '/api/auth/v1/loginsocial/',
  '/api/auth/v1/token/refresh/',
];

// ---------------------------------------------------------------------------
// Shared in-flight refresh — multiple queries can 401 at the same moment (e.g.
// several widgets fetching on page load). Without this, each one would fire
// its own POST /token/refresh/ and, on failure, its own signOut()/toast.
// Callers that arrive while a refresh is already in flight just await it.
//
// Across tabs, every refresh also holds TOKEN_REFRESH_LOCK. The API rotates
// the refresh cookie and blacklists the old one, so two tabs refreshing with
// the same cookie would make one fail, and its 401 clears both cookies.
// ---------------------------------------------------------------------------

export const TOKEN_REFRESH_LOCK = 'moveris-token-refresh';

let refreshPromise: Promise<string | null> | null = null;

function refreshAccessToken(
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshResult = await withCrossTabLock(TOKEN_REFRESH_LOCK, async () =>
        rawBaseQuery({ url: '/api/auth/v1/token/refresh/', method: 'POST' }, api, extraOptions),
      );
      if (refreshResult.data === undefined) return null;
      const refreshData = isEnvelope(refreshResult.data)
        ? refreshResult.data.data
        : refreshResult.data;
      return (refreshData as { access_token: string }).access_token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Reauth wrapper
// 401 on a protected endpoint → try POST /token/refresh/ (HttpOnly cookie,
//   shared across concurrent 401s — see refreshAccessToken above)
//   success → update token in store, retry original request
//     still 401 on retry → account itself isn't allowed here (e.g. demo
//     accounts blocked from the portal) — signOut, toast once
//   failure → signOut
// 401 on an auth endpoint → pass through (wrong credentials, not expired session)
// 402 → setAccountSuspended
// 403 → toast only
// ---------------------------------------------------------------------------

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQueryWithEnvelope(args, api, extraOptions);

  if (result.error?.status === 401) {
    const url = typeof args === 'string' ? args : args.url;
    const isAuthEndpoint = AUTH_ENDPOINTS.some((ep) => url.includes(ep));

    if (!isAuthEndpoint) {
      const newToken = await refreshAccessToken(api, extraOptions);

      if (newToken) {
        const currentUser = (api.getState() as RootState).auth?.user;
        if (currentUser) {
          api.dispatch(setCredentials({ token: newToken, user: currentUser }));
        } else {
          api.dispatch(setToken(newToken));
        }
        result = await baseQueryWithEnvelope(args, api, extraOptions);

        // Check right before acting — another concurrent 401 may have already
        // signed out while this request's retry was in flight; skip the
        // redundant toast/dispatch if so.
        const alreadySignedOut = (api.getState() as RootState).auth?.token === null;
        if (result.error?.status === 401 && !alreadySignedOut) {
          // Refresh succeeded, but the retried request is still 401 — this isn't a
          // stale-token problem, the account itself isn't allowed here (e.g. demo
          // accounts blocked from the portal). Sign out instead of looping forever
          // with a valid-looking session where every request silently fails.
          const code = (result.error.data as { code?: string } | undefined)?.code;
          notify.error(
            code === 'not_authorized_user_to_portal'
              ? 'Your account does not have access to the developer portal.'
              : 'Your session is no longer valid. Please sign in again.',
          );
          api.dispatch(signOut());
        }
      } else if ((api.getState() as RootState).auth?.token !== null) {
        api.dispatch(signOut());
      }
    }
  }

  if (result.error) {
    const { status } = result.error;
    if (status === 402) {
      api.dispatch(setAccountSuspended(true));
    } else if (status === 403) {
      notify.error('You do not have permission to perform this action.');
    }
  }

  return result;
};

// ---------------------------------------------------------------------------
// Base API — all feature endpoints inject into this single instance
// ---------------------------------------------------------------------------

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'ApiKey',
    'Webhook',
    'Usage',
    'Billing',
    'CreditPlan',
    'Transaction',
    'Detection',
    'Analytics',
    'Activity',
    'Organization',
    'AllowedHost',
    'McpSession',
    'AdminUser',
    'AdminApiKey',
    'AdminUsageLog',
    'AdminDetection',
    'AdminOrganization',
    'ThresholdOverride',
    'ThresholdAuditLog',
    'ModelRegistry',
  ],
  endpoints: () => ({}),
});
